import { ReactElement, useEffect, useMemo, useState } from "react";

import { Checkbox } from "@/components/_ui/checkbox";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/_ui/command";
import { cn } from "@/lib/cn";

import { useDatatableContext } from "../use-datatable-context";

import { FilterByTextListPayload, FilterByTextListPayloadSchema } from "./functions/filter-by-text-list";

export interface FilterByTextListItem {
  id: string;
  name: string;
}

export interface FilterByTextListConfigurationViewProps<TItem extends FilterByTextListItem> {
  column: {
    id: string;
  };
  itemList: TItem[];
  renderItem: (item: TItem | null, view: "icon" | "text" | "icon-text") => ReactElement;
  onFinished: () => void;
}

export const FilterByTextListConfigurationView = <TItem extends FilterByTextListItem>({
  onFinished,
  itemList,
  renderItem,
  ...props
}: FilterByTextListConfigurationViewProps<TItem>) => {
  const { table, state, getConfigurationForColumn } = useDatatableContext();
  const { columnFilters } = state;

  const columnConfiguration = getConfigurationForColumn(props.column.id);

  const [selectedItemList, setSelectedItemList] = useState<(FilterByTextListItem | null)[] | null>(null);

  const column = table.getColumn(props.column.id);

  const filterValue = useMemo(() => {
    const filterValue = column?.getFilterValue();

    const safePayload = FilterByTextListPayloadSchema.safeParse(filterValue);
    if (!safePayload.success) {
      return null;
    }

    return safePayload.data;

    // We also listen to the columnFilters to update the filter value when the user changes the filter mode
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [column, columnFilters]);

  useEffect(() => {
    if (!filterValue) {
      return;
    }

    // we expect the filter-by-list to contain the ids of the items not the display values
    setSelectedItemList(
      filterValue.textList.flatMap((id) => {
        const item = itemList.find((item) => item.id === id);
        return item ? [{ id: item.id, name: item.name }] : [];
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedItemList) {
      return;
    }
    if (selectedItemList.length === 0) {
      clearFilter();
      return;
    }

    const newFilterValue: FilterByTextListPayload = {
      type: "filter_by_text_list",
      mode: filterValue?.mode ?? "included",
      textList: selectedItemList.map((item) => item?.id ?? null),
    };

    let shouldUpdate = false;
    if (filterValue?.mode !== newFilterValue.mode) {
      shouldUpdate = true;
    }

    if (JSON.stringify(filterValue?.textList.sort()) !== JSON.stringify(newFilterValue.textList.sort())) {
      shouldUpdate = true;
    }

    if (!shouldUpdate) {
      return;
    }
    // IMPORTANT: We filter by the id of the item not the name
    setFilter(newFilterValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemList]);

  const setFilter = (payload: FilterByTextListPayload) => {
    column?.setFilterValue(payload);
  };

  const clearFilter = () => {
    if (!column) {
      return;
    }
    table.setColumnFilters(columnFilters.slice().filter((filterValue) => filterValue.id !== column.id));
  };

  const handleSelect = (item: FilterByTextListItem | null, shouldFinish?: boolean) => {
    // Please not that we are not filtering by id by by the name property of the item
    const itemList = selectedItemList ?? [];
    const updatedItemList = itemList.slice().filter((i) => i?.id !== item?.id); // avoid duplicates
    setSelectedItemList([...updatedItemList, item]);

    if (shouldFinish) {
      setTimeout(function () {
        onFinished();
      }, 1);
    }
  };

  const handleDeselect = (item: FilterByTextListItem | null, shouldFinish?: boolean) => {
    const itemList = selectedItemList ?? [];
    const updatedItemList = itemList.slice().filter((i) => i?.id !== item?.id);
    setSelectedItemList(updatedItemList);

    if (shouldFinish) {
      setTimeout(function () {
        onFinished();
      }, 1);
    }
  };

  return (
    <Command>
      <CommandInput placeholder={columnConfiguration?.getDisplayValue() ?? "Filtern"} />
      <CommandList>
        <CommandGroup>
          {[null].map((_) => {
            const isSelected = !!selectedItemList && selectedItemList.includes(null);
            const handleAction = isSelected ? handleDeselect : handleSelect;

            return (
              <CommandItem
                key={null}
                value={"Nicht zugewiesen"}
                className="group flex flex-row items-center gap-1 p-1 py-1.5 hover:cursor-pointer"
                onSelect={() => {
                  handleAction(null, true);
                }}
              >
                <Checkbox
                  className={cn([
                    "rounded-md border border-gray-300 group-hover:opacity-100",
                    isSelected ? "" : " opacity-0",
                  ])}
                  checked={isSelected}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAction(null);
                  }}
                />
                <div className="h-3.5 w-3.5 rounded-full">{renderItem(null, "icon-text")}</div>
              </CommandItem>
            );
          })}

          {itemList.map((item) => {
            const isSelected = !!selectedItemList && !!selectedItemList.find((i) => i?.id === item.id);
            const handleAction = isSelected ? handleDeselect : handleSelect;

            return (
              <CommandItem
                key={item.id}
                value={item.name}
                className="group flex flex-row items-center gap-1 p-1 py-1.5 hover:cursor-pointer"
                onSelect={() => {
                  handleAction(item, true);
                }}
              >
                <Checkbox
                  className={cn([
                    "rounded-md border border-gray-300 group-hover:opacity-100",
                    isSelected ? "" : " opacity-0",
                  ])}
                  checked={isSelected}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAction(item);
                  }}
                />
                <div className="h-3.5 w-3.5">{renderItem(item, "icon-text")}</div>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );
};
